import {
  type InjectionKey,
  type MaybeRefOrGetter,
  computed,
  inject,
  provide,
  ref,
  toValue,
} from 'vue';

export type TreeNodeId = number | string;

export type TreeExpansionApi = {
  isExpanded: (id: TreeNodeId) => boolean;
  toggle: (id: TreeNodeId) => void;
};

const treeExpansionKey: InjectionKey<TreeExpansionApi> = Symbol(
  'bitts-tree-expansion',
);

export function useTreeExpansion(options: {
  initialExpandedNodes: Record<TreeNodeId, boolean>;
  forceExpandedIds: MaybeRefOrGetter<TreeNodeId[]>;
  isExpansionLocked: MaybeRefOrGetter<boolean>;
}): TreeExpansionApi {
  const inherited = inject(treeExpansionKey, null);
  if (inherited) return inherited;

  const userExpanded = ref<Record<TreeNodeId, boolean>>({
    ...options.initialExpandedNodes,
  });
  const forcedIds = computed(() => new Set(toValue(options.forceExpandedIds)));

  const api: TreeExpansionApi = {
    isExpanded: (id) => forcedIds.value.has(id) || !!userExpanded.value[id],
    toggle: (id) => {
      if (toValue(options.isExpansionLocked)) return;
      userExpanded.value[id] = !userExpanded.value[id];
    },
  };

  provide(treeExpansionKey, api);
  return api;
}
